import * as React from 'react';
import * as wijmo from '@grapecity/wijmo';

import * as wjChart from '@grapecity/wijmo.react.chart';

import { Col, Row, Container, Form } from 'react-bootstrap';
import { useEffect, useState, useRef } from 'react';
import { toInt, toFloat, calcRate } from 'utils/numberUtils';
import { isBeforeThisMonth } from 'utils/dateUtils';
import './Analysis.css';
import { AnalysisKind, calcMajorUnit, calcMaxValue } from 'views/Analysis/analysisUtils';

// 昨対比の比較グラフ
function AnalysisGlaphCurrentBefore(props) {
  // 分析データリスト
  const analysisList = props.analysisList;
  // 選択中の期
  const selectedPeriod = props.selectedPeriod;
  // 集計項目
  const summaryItems = props.summaryItems;
  // 分析種別
  const analysisKind = props.analysisKind;
  // 比較種別
  const comparisonKind = props.comparisonKind;
  // プレフィックス
  const prefix = analysisKind.prefix;
  // カテゴリ名
  const categoryName = props.categoryName;

  // 比較用のキー名
  const key1 = comparisonKind.key1;
  const key2 = comparisonKind.key2;

  // 合計用のキー名
  const keyTotal = props.keyTotal;

  // 前期の件数
  const [beforeCounts, setBeforeCounts] = useState([]);
  // 今期の件数
  const [currentCounts, setCurrentCounts] = useState([]);

  // 契約率
  const [keiRate, setKeiRate] = useState([]);

  // 集計範囲の切り替え状態
  const [rangeType, setRangeType] = useState('all');
  // 集計対象データを切り替え
  const getFilteredCollection = () => {
    if (rangeType === 'untilThisMonth') {
      return analysisList.sourceCollection.filter((item) =>
        isBeforeThisMonth(item.MONTH, selectedPeriod)
      );
    }
    return analysisList.sourceCollection;
  };

  useEffect(() => {
    // 集計単位（全期間・今月まで）に合わせてフィルタ
    const filteredCollection = getFilteredCollection();

    // 前期の全体の合計
    let beforeAllTotal = filteredCollection.reduce(
      (sum, item) => sum + toInt(item[`${prefix}${key1}_${keyTotal}`], 0),
      0
    );
    // 前期の件数
    setBeforeCounts(
      summaryItems
        .filter((x) => !x.ignoreCalcTotal)
        .map((x) => {
          let beforeTotal = filteredCollection.reduce(
            (sum, item) => sum + toInt(item[`${prefix}${key1}_${x.key}`], 0),
            0
          );
          return {
            category: x.label,
            value: beforeTotal,
            rate: calcRate(beforeTotal, beforeAllTotal, 3),
          };
        })
    );

    // 今期の全体の合計
    let currentAllTotal = filteredCollection.reduce(
      (sum, item) => sum + toInt(item[`${prefix}${key2}_${keyTotal}`], 0),
      0
    );

    // 今期の件数
    setCurrentCounts(
      summaryItems
        .filter((x) => !x.ignoreCalcTotal)
        .map((x) => {
          let cunrrentTotal = filteredCollection.reduce(
            (sum, item) => sum + toInt(item[`${prefix}${key2}_${x.key}`], 0),
            0
          );
          return {
            category: x.label,
            value: cunrrentTotal,
            rate: calcRate(cunrrentTotal, currentAllTotal, 3),
          };
        })
    );

    // 契約件数の場合は契約率を算出
    if (analysisKind.key == AnalysisKind.KEIYAKU) {
      setKeiRate(
        summaryItems.map((x) => {
          let beforeTotal = filteredCollection.reduce(
            (sum, item) => sum + toInt(item[`KEI_BEFORE_${x.key}`], 0),
            0
          );
          let beforeSoudanTotal = filteredCollection.reduce(
            (sum, item) => sum + toInt(item[`BEFORE_${x.key}`], 0),
            0
          );
          let currentTotal = filteredCollection.reduce(
            (sum, item) => sum + toInt(item[`KEI_CURRENT_${x.key}`], 0),
            0
          );
          let currentSoudanTotal = filteredCollection.reduce(
            (sum, item) => sum + toInt(item[`CURRENT_${x.key}`], 0),
            0
          );

          let beforeRate = calcRate(beforeTotal, beforeSoudanTotal, 3) * 100;
          let currentRate = calcRate(currentTotal, currentSoudanTotal, 3) * 100;

          return {
            category: x.label,
            before: toFloat(beforeRate, 1),
            current: toFloat(currentRate, 1),
          };
        })
      );
    }
  }, [analysisList, analysisKind, summaryItems, comparisonKind, rangeType, props.currentMonth]);

  return (
    <>
      {/* 集計範囲切り替え*/}
      <Row>
        <Col md="3">
          <div style={{ width: '120px' }}>
            <label className="item-header-label">集計範囲</label>
            <Form.Control
              as="select"
              value={rangeType}
              onChange={(e) => setRangeType(e.target.value)}
            >
              <option value="all">全期間</option>
              <option value="untilThisMonth">今月まで</option>
            </Form.Control>
          </div>
        </Col>
      </Row>
      <Row className="mt-2">
        {/* 前期の全体構成の円グラフ */}
        <Col className="pl-3" md="6">
          <wjChart.FlexPie
            header={`${selectedPeriod - 1}期 ${categoryName}（${analysisKind.value}）【累計】`}
            style={{ backgroundColor: '#f7f7f8', borderRadius: '8px' }}
            itemsSource={beforeCounts}
            bindingName="category"
            binding="value"
            startAngle={90}
          >
            <wjChart.FlexPieDataLabel
              content={(ht) => {
                return ht.item.rate > 0.01
                  ? `${ht.name}(${wijmo.format('{val:p1}', { val: ht.item.rate })})`
                  : '';
              }}
              position="Outside"
            />
          </wjChart.FlexPie>
        </Col>
        {/* 今期の全体構成の円グラフ */}
        <Col className="pl-3" md="6">
          <wjChart.FlexPie
            header={`${selectedPeriod}期 ${categoryName}（${analysisKind.value}）【累計】`}
            style={{ backgroundColor: '#f7f7f8', borderRadius: '8px' }}
            itemsSource={currentCounts}
            bindingName="category"
            binding="value"
            startAngle={90}
          >
            <wjChart.FlexPieDataLabel
              content={(ht) => {
                return ht.item.rate > 0.01
                  ? `${ht.name}(${wijmo.format('{val:p1}', { val: ht.item.rate })})`
                  : '';
              }}
              isContentHtml={true}
              position="Outside"
            />
          </wjChart.FlexPie>
        </Col>
      </Row>
      <Row className="mt-1">
        {/* 件数グラフ */}
        <Col md={summaryItems.length <= 10 ? '6' : '12'} className="pl-3">
          <wjChart.FlexChart
            header={`${comparisonKind.value} ${categoryName}（${analysisKind.value}）推移`}
            chartType="Column"
            bindingX="category"
            itemsSource={summaryItems
              .filter((x) => !x.ignoreCalcTotal)
              .map((item, i) => ({
                category: item.label,
                before: beforeCounts[i]?.value ?? 0,
                current: currentCounts[i]?.value ?? 0,
              }))}
            dataLabel={{
              content: '{y}',
            }}
            style={{ backgroundColor: '#f7f7f8', borderRadius: '8px' }}
            options={{
              groupWidth: '52px', // 棒グラフの幅
            }}
          >
            {/* Y軸の設定 */}
            <wjChart.FlexChartAxis
              wjProperty="axisY"
              format="n0"
              max={calcMaxValue([...beforeCounts, ...currentCounts])}
              majorUnit={calcMajorUnit([...beforeCounts, ...currentCounts])}
            />
            <wjChart.FlexChartSeries name={`${selectedPeriod - 1}期`} binding="before" />
            <wjChart.FlexChartSeries name={`${selectedPeriod}期`} binding="current" />
          </wjChart.FlexChart>
        </Col>

        {/* 契約率グラフ */}
        {analysisKind.key == AnalysisKind.KEIYAKU && (
          <Col md={summaryItems.length <= 10 ? '6' : '12'}>
            <wjChart.FlexChart
              header={`${comparisonKind.value} ${categoryName} 契約率推移`}
              chartType="Column"
              bindingX="category"
              itemsSource={keiRate}
              dataLabel={{
                content: '{y}%',
              }}
              style={{ backgroundColor: '#f7f7f8', borderRadius: '8px' }}
              options={{
                groupWidth: '52px', // 棒グラフの幅
              }}
            >
              {/* Y軸の設定 */}
              <wjChart.FlexChartAxis wjProperty="axisY" format="n0" max={100} majorUnit={10} />
              <wjChart.FlexChartSeries
                name={`${selectedPeriod - 1}期`}
                binding="before"
              ></wjChart.FlexChartSeries>
              <wjChart.FlexChartSeries
                name={`${selectedPeriod}期`}
                binding="current"
              ></wjChart.FlexChartSeries>
            </wjChart.FlexChart>
          </Col>
        )}
      </Row>
    </>
  );
}
export default AnalysisGlaphCurrentBefore;
